import { Outlet, NavLink, useLocation } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Users, FileText, LayoutDashboard } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { PageTransition } from "@/components/carmel/PageTransition";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  icon?: LucideIcon;
}

const adminNavItems: NavItem[] = [
  { to: "/admin", label: "Organisation", end: true },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/roles", label: "Roles" },
  { to: "/admin/departments", label: "Departments" },
  { to: "/admin/teams", label: "Teams" },
];

const oversightNavItems: NavItem[] = [
  { to: "/oversight", label: "Overview", end: true, icon: LayoutDashboard },
  { to: "/oversight/interventions", label: "Interventions", icon: AlertTriangle },
  { to: "/oversight/users", label: "Users", icon: Users },
  { to: "/oversight/reports", label: "Reports", icon: FileText },
];

export function AdminPortalLayout() {
  const location = useLocation();
  const isOversight = location.pathname.startsWith("/oversight");
  const navItems = isOversight ? oversightNavItems : adminNavItems;
  const sectionLabel = isOversight ? "Oversight" : "Organisation Portal";

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[var(--background)]">
      <aside className="hidden h-screen w-[var(--sidebar-width)] flex-col border-r border-[var(--border)] bg-[var(--card)] md:flex">
        <div className="flex h-16 items-center border-b border-[var(--border)] px-4">
          <NavLink
            to="/home"
            className="flex items-center gap-2 text-sm font-medium text-[var(--color-body)] hover:text-[var(--foreground)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to app
          </NavLink>
        </div>

        <div className="px-4 pt-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-mute)]">
            {sectionLabel}
          </h2>
        </div>

        <nav className="mt-2 flex-1 space-y-1 p-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium transition-all duration-150",
                  isActive
                    ? "bg-[var(--color-primary-soft)] text-[var(--color-primary-active)]"
                    : "text-[var(--color-body)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]",
                )
              }
            >
              {"icon" in item && item.icon && <item.icon className="h-4 w-4" />}
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center border-b border-[var(--border)] bg-[var(--card)] px-6">
          <h1 className="text-sm font-semibold text-[var(--foreground)]">
            {sectionLabel}
          </h1>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 scroll-container">
          <PageTransition><Outlet /></PageTransition>
        </main>
      </div>
    </div>
  );
}
